// @ts-nocheck
import * as turf from '@turf/turf';
import { uniq } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { useDidUpdate } from 'sonata-react-components';

import { getReportingStreamStatsUserId } from '@/modules/reporting/reporting.resources';
import { reportingToggleMapLoading } from '@/store/actions/reporting.actions';
import { getCampaignsScreens, getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getAdvancedFilters, getCampaigns, getCurrency } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { getFontAwesomeIcon, getFontAwesomeStyleCode } from '@/ts-modules/reporting/Map/helpers';
import { IconLayer } from '@/ts-modules/reporting/Map/IconLayer';

const ITEMS_SOURCE_ID = 'campaignItems_';
const ITEMS_OUTER_COLOR_LAYER_SUFFIX_ID = '_outer_color';
const ITEMS_OUTER_BORDER_LAYER_SUFFIX_ID = '_outer_border';
const ITEMS_INNER_COLOR_LAYER_SUFFIX_ID = '_inner_color';
const ITEMS_ICON_LAYER_SUFFIX_ID = '_icon';

function ScreensStoresLayer({ mapId, section, type }) {
  const dispatch = useDispatch();
  const tokens = {
    storesStats: {}
  };

  const { current: map } = useMap();
  const { getMap, getMapCartographyFilter, getMapEvents, getMapScreensData, getMapStoresData, setMapSettings } =
    useReportingMap();
  const { settings } = getMap({ id: mapId });
  const hasFitScreensBounds = useRef(false);
  const selectedCampaignIds = useSelector(getCampaigns);
  const reportingCampaigns = useSelector(getSelectedCampaigns);
  const campaignScreens = useSelector(getCampaignsScreens);
  const generalFilters = useSelector(store => getAdvancedFilters(store, section));
  const currentCurrency = useSelector(store => getCurrency(store, section));
  const user = useSelector(getCurrentUser);

  const {
    cartographyLevel: generalSectionCartographyLevel,
    cartographyLevelIds: generalCartographyLevelIds,
    timeFrame,
    timeZone: timezone,
    variableType,
    variableValues
  } = generalFilters;

  const cartographyFilter = getMapCartographyFilter({ id: mapId });
  const { level: cartographyLevel, shapeIds: cartographyLevelIds } = cartographyFilter;
  const mapCartographyLevel = cartographyLevel || generalSectionCartographyLevel;
  const mapCartographyLevelIds = cartographyLevelIds.length ? cartographyLevelIds : generalCartographyLevelIds;
  const cartography = mapCartographyLevelIds.length ? { [mapCartographyLevel]: mapCartographyLevelIds } : undefined;
  const variable = variableType && variableValues.length ? { [variableType]: variableValues } : undefined;
  const campaigns = reportingCampaigns.filter(reportingCampaign => selectedCampaignIds.includes(reportingCampaign.id));
  const mapEvents = getMapEvents({ id: mapId });

  const getGroupDimension = () => {
    switch (type) {
      case 'screens':
        return 'screenId';
      case 'stores':
        return 'attrVids';
      default:
        return '';
    }
  };

  const getStoresStatsBody = () => {
    return {
      opts: {
        currencyOptions: {
          currency: currentCurrency
        }
      },
      params: {
        dateInterval: {
          timezone,
          ...timeFrame
        },
        filters: {
          ...(cartography && { cartography }),
          ...variable
        },
        groups: [getGroupDimension()],
        outputMetrics: mapEvents
      }
    };
  };

  const screenGroups = reportingCampaigns
    .filter(reportingCampaign => selectedCampaignIds.includes(reportingCampaign.id))
    .reduce((totalAcc, reportingCampaign) => {
      return totalAcc.concat(
        reportingCampaign.screenGroups.reduce((acc, screenGroup) => {
          if (!acc.includes(screenGroup._id)) {
            return acc.concat(screenGroup);
          }
          return acc;
        }, totalAcc)
      );
    }, []);

  const getScreensData = () => {
    return campaignScreens
      .filter(({ deleted }) => !deleted)
      .map(campaignScreen => {
        const screenGroup = screenGroups.find(sG => campaignScreen.groups.includes(sG._id));
        return {
          ...campaignScreen,
          ...(screenGroup && {
            color: screenGroup.color,
            icon: getFontAwesomeIcon(screenGroup.icon, 'screensLayer'),
            iconStyleCode: getFontAwesomeStyleCode(screenGroup.icon)
          })
        };
      });
  };

  const getItemsData = async () => {
    dispatch(reportingToggleMapLoading(mapId, true));
    const storesItems =
      type === 'screens'
        ? getScreensData()
        : campaigns.reduce(
            (prev, campaign) => [
              ...prev,
              ...campaign.zones.filter(({ type: zoneType }) => zoneType === 'attributionTorus')
            ],
            []
          );
    const storesStats = await getReportingStreamStatsUserId(
      getStoresStatsBody(),
      tokens.storesStats,
      user.id,
      campaigns.map(campaign => campaign.id).join(','),
      'none',
      mapEvents
    );
    const id = type === 'screens' ? 'device_id' : 'shop';
    const groupField = type === 'screens' ? 'screenId' : 'attrVids';
    const storesItemsFiltered = storesItems.filter(item => storesStats.map(i => i[groupField]).includes(item[id]));
    dispatch(reportingToggleMapLoading(mapId, false));
    if (type === 'screens' && !hasFitScreensBounds.current && storesItemsFiltered.length && map) {
      hasFitScreensBounds.current = true;
      const screensBbox = turf.bbox(turf.points(storesItemsFiltered.map(item => [item.longitude, item.latitude])));
      map.fitBounds(screensBbox, { padding: 50 });
    }
    setMapSettings({
      id: mapId,
      value: {
        ...settings,
        cartography: {
          ...settings.cartography,
          status: 'idle'
        },
        [type]: {
          ...settings[type],
          data: {
            ...settings[type].data,
            items: storesItemsFiltered,
            stats: storesStats
          }
        }
      }
    });
  };

  useEffect(() => {
    if (type === 'stores' || type === 'screens') {
      getItemsData();
    }
  }, [mapEvents, type, campaignScreens]);

  useDidUpdate(() => {
    if (settings.cartography.status === 'reload') {
      getItemsData();
    }
  }, [settings.cartography.status]);

  const getFormattedData = fn => {
    const items = fn({ id: mapId });
    return items.map(item => ({
      ...item,
      icon: getFontAwesomeIcon(item.icon, 'screensLayer'),
      iconStyleCode: getFontAwesomeStyleCode(item.icon)
    }));
  };

  const items = type === 'screens' ? getFormattedData(getMapScreensData) : getFormattedData(getMapStoresData);

  const sourceData = turf.featureCollection(
    items.reduce((array, item) => {
      const itemId = item.device_id || item._id;
      return array.concat(
        turf.feature(
          item.polygon || turf.point([item.longitude, item.latitude]).geometry,
          {
            audienceRadius: item.audienceRadius,
            color: item.color,
            icon: item.icon,
            iconStyleCode: item.iconStyleCode,
            id: itemId,
            latitude: item.latitude,
            longitude: item.longitude,
            name: itemId,
            tags: item.tags
          },
          { id: itemId }
        )
      );
    }, [])
  );

  useEffect(() => {
    const uniqueIcons = uniq(items.filter(item => !!item.icon).map(item => item.icon));
    const noLoadedIcons = uniqueIcons.filter(icon => !map.hasImage(icon) && icon.charCodeAt(0).toString(16) === '0');
    if (noLoadedIcons.length) {
      noLoadedIcons.forEach(itemIcon => {
        map.loadImage(`${taptap.config.sonataLIStaticsUrlBase}/images/icons/${itemIcon}.png`, (error, imageResource) =>
          map.addImage(itemIcon, imageResource)
        );
      });
    }
  }, [items]);

  const SOURCE_ID = `${mapId}_${ITEMS_SOURCE_ID}${type}`;
  const ITEMS_OUTER_COLOR_LAYER_ID = `${SOURCE_ID}${ITEMS_OUTER_COLOR_LAYER_SUFFIX_ID}`;
  const ITEMS_OUTER_BORDER_LAYER_ID = `${SOURCE_ID}${ITEMS_OUTER_BORDER_LAYER_SUFFIX_ID}`;
  const ITEMS_INNER_COLOR_LAYER_ID = `${SOURCE_ID}${ITEMS_INNER_COLOR_LAYER_SUFFIX_ID}`;
  const ITEMS_ICON_LAYER_ID = `${SOURCE_ID}${ITEMS_ICON_LAYER_SUFFIX_ID}`;

  return (
    <Source data={sourceData} id={SOURCE_ID} key={SOURCE_ID} type="geojson">
      <Layer
        id={ITEMS_OUTER_COLOR_LAYER_ID}
        key={ITEMS_OUTER_COLOR_LAYER_ID}
        paint={{
          'circle-color': ['get', 'color'],
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 12, 6, 14, 10, 16, 14]
        }}
        type="circle"
      />
      <Layer
        id={ITEMS_OUTER_BORDER_LAYER_ID}
        key={ITEMS_OUTER_BORDER_LAYER_ID}
        paint={{
          'circle-color': '#ffffff',
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 2, 12, 4, 14, 8, 16, 12]
        }}
        type="circle"
      />
      <Layer
        id={ITEMS_INNER_COLOR_LAYER_ID}
        key={ITEMS_INNER_COLOR_LAYER_ID}
        paint={{
          'circle-color': ['get', 'color'],
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 1, 12, 3, 14, 7, 16, 11]
        }}
        type="circle"
      />
      {['far', 'fal', 'fak', 'fas'].map(styleCode => (
        <IconLayer
          id={ITEMS_ICON_LAYER_ID}
          key={`iconLayer_${styleCode}`}
          source={`${ITEMS_SOURCE_ID}${type}`}
          styleCode={styleCode}
        />
      ))}
    </Source>
  );
}

ScreensStoresLayer.propTypes = {
  mapId: PropTypes.string.isRequired,
  section: PropTypes.string,
  type: PropTypes.oneOf(['screens', 'stores']).isRequired
};

ScreensStoresLayer.defaultProps = {
  section: 'general'
};

export default ScreensStoresLayer;
